import { useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  Divider,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  firstNameRegex,
  lastNameRegex,
  emailRegex,
  strongPasswordRegex,
} from "../../utils/authUtils";
import  Google from '../../assets/images/Google.png';

export default function SignupForm({
  signupData,
  setSignupData,
  emailValid,
  setEmailvalid,
  passwordValid,
  setPasswordValid,
  firstNameValid,
  setFirstNameValid,
  lastNameValid,
  setLastNameValid,
  confirmPassword,
  setConfirmPassword,
  showPassword,
  setShowPassword,
  showConfirmPassword,
  setShowConfirmPassword,
  handleSignup,
  signUpFailedError,
  loading,
  signupDataEntered,
  verifyCode,
  setVerifyCode,
  verifyCodeMismatch,
  handleVerifyClick,
  handleGoogleLogin
}) {
  /* First name validation */
  useEffect(() => {
    if (signupData.firstName.trim() === "") {
      setFirstNameValid(true);
    } else if (!firstNameRegex.test(signupData.firstName)) {
      setFirstNameValid(false);
    } else {
      setFirstNameValid(true);
    }
  }, [signupData.firstName, setFirstNameValid]);

  /* last name validation */
  useEffect(() => {
    if (signupData.lastName.trim() === "") {
      setLastNameValid(true);
    } else if (!lastNameRegex.test(signupData.lastName)) {
      setLastNameValid(false);
    } else {
      setLastNameValid(true);
    }
  }, [signupData.lastName, setLastNameValid]);

  /* email validation */
  useEffect(() => {
    if (signupData.email.trim() === "") {
      setEmailvalid((prev) => ({ ...prev, signup: true }));
    } else if (!emailRegex.test(signupData.email)) {
      setEmailvalid((prev) => ({ ...prev, signup: false }));
    } else {
      setEmailvalid((prev) => ({ ...prev, signup: true }));
    }
  }, [signupData.email, setEmailvalid]);

  /* password validation */
  useEffect(() => {
    if (signupData.password.trim() === "") {
      setPasswordValid((prev) => ({ ...prev, signup: true }));
    } else if (!strongPasswordRegex.test(signupData.password)) {
      setPasswordValid((prev) => ({ ...prev, signup: false }));
    } else {
      setPasswordValid((prev) => ({ ...prev, signup: true }));
    }
  }, [signupData.password, setPasswordValid]);

  /* confirm password validation */
  useEffect(() => {
    if (signupData.confirmPassword.trim() === "") {
      setConfirmPassword(true);
    } else if (signupData.password !== signupData.confirmPassword) {
      setConfirmPassword(false);
    } else {
      setConfirmPassword(true);
    }
  }, [signupData.confirmPassword, signupData.password, setConfirmPassword]);

  /* Show singup form - Before entering details for signup  */
  if (!signupDataEntered) {
    return (
      <Box component="form">
        {signUpFailedError && <Alert severity="error">{signUpFailedError}</Alert>}
        <TextField
          label="First Name"
          fullWidth
          margin="normal"
          value={signupData.firstName}
          onChange={(e) => setSignupData({ ...signupData, firstName: e.target.value })}
          error={!firstNameValid}
          helperText={!firstNameValid && "Enter valid first name (only alphabets with at least 3 chars)"}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextField
          label="Last Name"
          fullWidth
          margin="normal"
          value={signupData.lastName}
          onChange={(e) => setSignupData({ ...signupData, lastName: e.target.value })}
          error={!lastNameValid}
          helperText={!lastNameValid && "Enter valid last name (only alphabets with at least 1 char)"}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextField
          label="Email Address"
          fullWidth
          margin="normal"
          type="email"
          value={signupData.email}
          onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
          error={!emailValid.signup}
          helperText={!emailValid.signup && "Enter a valid email address"}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextField
          label="Password"
          fullWidth
          margin="normal"
          type={showPassword ? "text" : "password"}
          value={signupData.password}
          onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
          error={!passwordValid.signup}
          helperText={
            !passwordValid.signup &&
            "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character."
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" aria-label="Toggle password visibility">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          slotProps={{ htmlInput: { maxLength: 14 } }}
        />
        <TextField
          label="Confirm Password"
          fullWidth
          margin="normal"
          type={showConfirmPassword ? "text" : "password"}
          value={signupData.confirmPassword}
          onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
          error={!confirmPassword}
          helperText={!confirmPassword && "Confirm password mismatch"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" aria-label="Toggle confirm password visibility">
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          slotProps={{ htmlInput: { maxLength: 14} }}
        />
        <Button
          fullWidth
          variant="contained"
          loading={loading}
          loadingPosition="end"
          disabled={
            !emailValid.signup ||
            !passwordValid.signup ||
            !firstNameValid ||
            !lastNameValid ||
            !signupData.firstName ||
            !signupData.lastName ||
            !signupData.email ||
            !signupData.password ||
            !signupData.confirmPassword ||
            !confirmPassword ||
            loading
          }
          onClick={handleSignup}
          sx={{ mt: 2, py: 1, fontSize: "medium", textTransform: "none" }}
        >
          Sign Up
        </Button>
        <Divider sx={{ my: 3 }}>
          <Typography sx={{ color: "gray" }}>or continue with</Typography>
        </Divider>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<img src={Google} alt="Google" style={{ width: 20, height: 20 }} />}
          onClick={handleGoogleLogin}
          sx={{ textTransform: "none", py: 1 }}
        >
          Sign Up with Google
        </Button>
      </Box>
    );
  }

  /* After signup - verfication code form */
  return (
    <Box>
      {verifyCodeMismatch && <Alert severity="error">Verification code mismatch</Alert>}
      <Typography sx={{ mt: 2, fontWeight: "bold", color: "gray" }}>
        Please check your {signupData.email} for verification code to confirm your account
      </Typography>
      <TextField
        fullWidth
        label="Enter 6 digit verification code"
        margin="normal"
        value={verifyCode}
        onChange={(e) => setVerifyCode(e.target.value)}
        slotProps={{ htmlInput: { maxLength: 6 } }}
      />
      <Button fullWidth variant="contained" onClick={handleVerifyClick} loading={loading}>
        Verify
      </Button>
    </Box>
  );
}
